import {
  Alert,
  AlertIcon,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@elkaso-app/web-design';
import { MasterProductFamily, useDeleteMasterProductFamilyApi } from 'apis/master-product-families';
import React, { useCallback } from 'react';

export interface IDeleteFamilyModalCell {
  trigger: JSX.Element;
  family: MasterProductFamily;
}

export const DeleteFamilyModal: React.FC<IDeleteFamilyModalCell> = ({ trigger, family }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const { mutate: deleteFamily, isLoading: isDeleting } = useDeleteMasterProductFamilyApi({
    onSuccess: () => {
      toast({ title: `Family "${family.universalName}" deleted`, status: 'success' });
      onClose();
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      const message =
        status === 409
          ? `Can't delete "${family.universalName}" — it still has variants. Delete or reassign them first.`
          : error?.response?.data?.message || 'Failed to delete family. Please try again.';
      toast({ title: message, status: 'error' });
    },
  });

  const handleConfirm = useCallback(() => {
    deleteFamily(family.id);
  }, [deleteFamily, family.id]);

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: 'full', md: 'md' }}
        scrollBehavior='inside'
        closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Family</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align='stretch'>
              <Alert status='warning'>
                <AlertIcon />
                This action cannot be undone. Are you sure you want to delete this family?
              </Alert>
              <Text>
                <strong>Universal Name:</strong> {family.universalName}
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant='outline' mr={3} onClick={onClose} isDisabled={isDeleting}>
              Cancel
            </Button>
            <Button colorScheme='red' onClick={handleConfirm} isLoading={isDeleting} loadingText='Deleting...'>
              Delete Family
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
